import { useState, useCallback, useMemo } from 'react';
import {
  getEarliestTimestamp,
  getLatestTimestamp,
  LineChartResponse,
  Series, trimLineChartResponsesByTime
} from "@/components/charts/lineChartResponse";

interface HighlightWindow {
  start: number;
  end: number;
}

export interface UseTimeWindowHighlightReturn {
  highlightWindow: HighlightWindow | null;
  isWindowScrolledOff: boolean;
  highlightSpecificTimeWindow: (startTime: number, endTime: number) => void;
  clearHighlight: () => void;
  createHighlightMarkArea: (color?: string) => Record<string, any> | null;
  createInsetOverlaySeries: (
    series: Series,
    currentData: LineChartResponse[],
    seriesConfig: InsetOverlayConfig
  ) => Record<string, any> | null;
}


export function useTimeWindowHighlight(
  data: LineChartResponse[],
): UseTimeWindowHighlightReturn {
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [highlightWindow, setHighlightWindow] = useState<HighlightWindow | null>(null);
  const [capturedWindowData, setCapturedWindowData] = useState<LineChartResponse[] | null>(null);

  const highlightSpecificTimeWindow = useCallback((startTime: number, endTime: number) => {
    if (!data || data.length === 0) return;

    const windowData = trimLineChartResponsesByTime(data, startTime, endTime);

    if (windowData.length === 0) return;

    setHighlightWindow({ start: startTime, end: endTime });
    setCapturedWindowData(windowData);
    setIsHighlighting(true);
  }, [data]);

  const clearHighlight = useCallback(() => {
    setIsHighlighting(false);
    setHighlightWindow(null);
    setCapturedWindowData(null);
  }, []);

  const isWindowScrolledOff = useMemo(() => {
    if (!isHighlighting || !highlightWindow || !data || data.length === 0) return false;
    return getEarliestTimestamp(data) > highlightWindow.start
  }, [isHighlighting, highlightWindow, data]);

  const createHighlightMarkArea = (color = 'rgba(59, 130, 246, 0.2)') => {
    const isVisible = isHighlighting && !isWindowScrolledOff
    if (!isVisible || !highlightWindow) return null;

    return {
      silent: true,
      itemStyle: {
        color,
        borderColor: '#3b82f6',
        borderWidth: 2
      },
      data: [[
        { xAxis: highlightWindow.start },
        { xAxis: highlightWindow.end }
      ]]
    };
  };

  const createInsetOverlaySeries = (
    series: Series,
    currentData: LineChartResponse[],
    seriesConfig: InsetOverlayConfig
  ) => {

    if (!capturedWindowData) {
      return null;
    }

    const capturedWindowSeries = capturedWindowData
      .flatMap(lineChartResponse => lineChartResponse.series)
      .find(capturedSeries => capturedSeries.name === series.name);

    if (!highlightWindow || !capturedWindowSeries || !currentData || currentData.length === 0) {
      return null;
    }

    const originalWindowDuration = highlightWindow.end - highlightWindow.start;
    const currentChartDuration = getLatestTimestamp(currentData) - getEarliestTimestamp(currentData);
    const widthPercentage = originalWindowDuration / currentChartDuration;

    const insetStartTime =  getEarliestTimestamp(currentData);
    const insetEndTime = insetStartTime + (currentChartDuration * widthPercentage);

    const overlayData = capturedWindowSeries.data.map(([_time, value], i) => {
      const progress = i / (capturedWindowSeries.data.length - 1);
      const mappedTime = insetStartTime + (insetEndTime - insetStartTime) * progress;
      return [mappedTime, value];
    });

    return {
      ...seriesConfig,
      data: overlayData,
      animation: false,
      z: 10,
      markArea: {
        silent: true,
        itemStyle: {
          color: 'transparent',
          borderColor: '#3b82f6',
          borderWidth: 2,
          borderType: 'solid'
        },
        label: {
          show: true,
          position: 'top',
          formatter: () => {
            const startDate = new Date(highlightWindow.start);
            const endDate = new Date(highlightWindow.end);
            return `Historical: ${startDate.toLocaleTimeString()} - ${endDate.toLocaleTimeString()}`;
          },
          color: '#3b82f6',
          fontSize: 10,
          fontWeight: 'bold'
        },
        data: [[
          { xAxis: insetStartTime },
          { xAxis: insetEndTime }
        ]]
      }
    };
  };

  return {
    highlightWindow,
    isWindowScrolledOff,
    highlightSpecificTimeWindow,
    clearHighlight,
    createHighlightMarkArea,
    createInsetOverlaySeries
  };
}

interface InsetOverlayConfig {
  type?: string;
  smooth?: boolean;
  symbol?: string;
  lineStyle?: any;
  areaStyle?: any;
  name?: string;
}
